import type { ApiErrorDetail, LocalizedText } from '../errors/errors.ts';
import type { PhoneProblem } from '../phone/phone.ts';

/**
 * Field-level validation messages (owner feedback after M6a, D93). Every refused field comes back
 * as `{ path, code, params, message: { ar, en } }`, and the apps show the message under the field
 * itself. Messages name the rule ("11 digits starting with 07"), never just "invalid".
 */
export type FieldIssueCode =
  | PhoneProblem
  | 'too_short'
  | 'too_long'
  | 'number_too_small'
  | 'number_too_large'
  | 'not_integer'
  | 'invalid_email'
  | 'invalid_choice'
  | 'invalid_id'
  | 'invalid_format'
  | 'unexpected_field'
  | 'code_six_digits'
  | 'number_required'
  | 'yes_no_required'
  | 'text_required'
  | 'setting_unchanged'
  | 'setting_date_past'
  | 'setting_date_too_far'
  | 'setting_above_limit'
  | 'setting_below_default'
  | 'printer_address'
  | 'item_code_format'
  | 'production_after_expiry'
  | 'photo_required'
  | 'invalid';

export type IssueParams = Readonly<Record<string, string | number>>;

/** A form-field detail: an `ApiErrorDetail` whose code is one of ours. */
export interface FieldIssue extends ApiErrorDetail {
  readonly code: FieldIssueCode;
}

const MESSAGES: Readonly<Record<FieldIssueCode, LocalizedText>> = {
  required: { ar: 'هذا الحقل مطلوب.', en: 'This field is required.' },
  too_short: {
    ar: 'قصير جداً: الحد الأدنى {{min}} أحرف.',
    en: 'Too short: at least {{min}} characters.',
  },
  too_long: {
    ar: 'طويل جداً: الحد الأقصى {{max}} حرفاً.',
    en: 'Too long: at most {{max}} characters.',
  },
  number_too_small: { ar: 'يجب ألا يقل عن {{min}}.', en: 'Must be at least {{min}}.' },
  number_too_large: { ar: 'يجب ألا يزيد عن {{max}}.', en: 'Must be at most {{max}}.' },
  not_integer: {
    ar: 'يجب أن يكون رقماً صحيحاً بدون كسور.',
    en: 'Must be a whole number without fractions.',
  },
  invalid_email: {
    ar: 'البريد الإلكتروني غير صحيح (مثال: name@example.com).',
    en: 'The email address is not valid (example: name@example.com).',
  },
  invalid_choice: { ar: 'اختر قيمة من القائمة.', en: 'Choose a value from the list.' },
  invalid_id: { ar: 'المعرّف غير صحيح.', en: 'The identifier is not valid.' },
  invalid_format: { ar: 'الصيغة غير صحيحة.', en: 'The format is not valid.' },
  unexpected_field: {
    ar: 'حقل غير متوقع في الطلب.',
    en: 'This field is not expected in the request.',
  },
  invalid: { ar: 'القيمة غير صحيحة.', en: 'The value is not valid.' },
  code_six_digits: {
    ar: 'الرمز يتكون من 6 أرقام.',
    en: 'The code has 6 digits.',
  },
  number_required: { ar: 'اكتب رقماً.', en: 'Enter a number.' },
  yes_no_required: { ar: 'اختر نعم أو لا.', en: 'Choose yes or no.' },
  text_required: { ar: 'اكتب نصاً.', en: 'Enter text.' },
  setting_unchanged: {
    ar: 'هذه هي القيمة المعمول بها الآن نفسها. غيّر القيمة أو ألغِ.',
    en: 'This is the value already in effect. Change the value or cancel.',
  },
  setting_date_past: {
    ar: 'لا يمكن أن يبدأ التغيير في وقت مضى. اختر «الآن» أو وقتاً لاحقاً.',
    en: 'A change cannot start in the past. Choose "now" or a later time.',
  },
  setting_date_too_far: {
    ar: 'يمكن جدولة التغيير لسنة واحدة قادمة على الأكثر.',
    en: 'A change can be scheduled at most one year ahead.',
  },
  setting_above_limit: {
    ar: 'يجب ألا تزيد عن الحد الأقصى المعمول به ({{max}}).',
    en: 'Must not be more than the maximum in effect ({{max}}).',
  },
  setting_below_default: {
    ar: 'يجب ألا تقل عن القيمة الافتراضية المعمول بها ({{min}}).',
    en: 'Must not be less than the default in effect ({{min}}).',
  },
  printer_address: {
    ar: 'اكتب عنوان IP للطابعة (مثل 192.168.1.50) أو اسمها على الشبكة، بدون رقم المنفذ.',
    en: "Type the printer's IP address (for example 192.168.1.50) or its network name, without the port.",
  },
  item_code_format: {
    ar: 'الرمز: حروف إنكليزية كبيرة وأرقام وشَرطة (-) فقط، يبدأ بحرف أو رقم، حتى {{max}} حرفاً (مثال: BEEF-01).',
    en: 'The code: capital English letters, digits and dashes (-) only, starting with a letter or digit, up to {{max}} characters (example: BEEF-01).',
  },
  production_after_expiry: {
    ar: 'تاريخ الإنتاج يجب ألا يكون بعد تاريخ الانتهاء.',
    en: 'The production date must not be after the expiry date.',
  },
  // W5-6: a photo from this device, or the photos a phone sent through the QR code.
  photo_required: {
    ar: 'صورة واحدة على الأقل: اختر صورة، أو صوّر بالهاتف عبر الرمز.',
    en: 'At least one photo: choose one, or take it with the phone through the code.',
  },
  phone_characters: {
    ar: 'رقم الهاتف يجب أن يحتوي على أرقام فقط (يُسمح بعلامة + في البداية).',
    en: 'The phone number may contain digits only (a + is allowed at the start).',
  },
  phone_iraqi_format: {
    ar: 'رقم الهاتف يجب أن يكون 11 رقماً ويبدأ بـ 07 (مثال: 07701234567). للرقم الأجنبي اكتب + ثم رمز الدولة.',
    en: 'The phone number must be 11 digits starting with 07 (example: 07701234567). For a foreign number, type + and the country code.',
  },
  phone_iraqi_mobile_only: {
    ar: 'أرقام العراق يجب أن تكون أرقام موبايل تبدأ بـ 07.',
    en: 'Iraqi numbers must be mobile numbers starting with 07.',
  },
  phone_foreign_needs_plus: {
    ar: 'اكتب الرقم الأجنبي بعلامة + بدل 00 (مثال: +44 20 7946 0958).',
    en: 'Type a foreign number with + instead of 00 (example: +44 20 7946 0958).',
  },
  phone_foreign_invalid: {
    ar: 'هذا الرقم الدولي غير صحيح. تأكد من رمز الدولة وعدد الأرقام.',
    en: 'This international number is not valid. Check the country code and the number of digits.',
  },
};

function fill(template: string, params: IssueParams | undefined): string {
  return template.replace(/\{\{(\w+)\}\}/g, (whole, name: string) =>
    params && name in params ? String(params[name]) : whole,
  );
}

export function fieldIssueMessage(code: FieldIssueCode, params?: IssueParams): LocalizedText {
  const template = MESSAGES[code];
  return { ar: fill(template.ar, params), en: fill(template.en, params) };
}

export function fieldIssue(path: string, code: FieldIssueCode, params?: IssueParams): FieldIssue {
  const message = fieldIssueMessage(code, params);
  return { path, code, ...(params ? { params } : {}), message, issue: message.en };
}

export function isIssueCode(value: unknown): value is FieldIssueCode {
  return typeof value === 'string' && value in MESSAGES;
}
